import { z } from 'zod/v4';

/**
 * ЕДИНЫЙ ДВИЖОК СЕГМЕНТАЦИИ АУДИТОРИИ.
 *
 * Один контракт для всех мест, где выбирается аудитория пользователей:
 *  - список users в админке (фильтр + bulk-действия)
 *  - рассылки (broadcasts) — кому слать
 *  - промокоды — кому доступен промокод
 *  - приватные прямые трансляции (livestream) — кто имеет доступ
 *
 * Модель — ДВА УРОВНЯ (без глубокой рекурсии):
 *   SegmentDefinition
 *     groupsOp: AND | OR            ← оператор МЕЖДУ группами
 *     groups: SegmentGroup[]
 *       conditionsOp: AND | OR      ← оператор ВНУТРИ группы
 *       conditions: SegmentCondition[]
 *
 * Пустой `groups` (или отсутствующий definition) означает «вся аудитория»
 * (никаких ограничений). Это важно для PUBLIC-трансляций и рассылок без фильтра.
 *
 * Резолвер (apps/backend/.../audience/segment-resolver.service.ts) транслирует
 * это дерево в Drizzle SQL `where` для таблицы `User`. Реестр полей ниже —
 * единственный источник правды о том, какие поля и операторы поддерживаются:
 * по нему UI рисует конструктор, а резолвер строит SQL.
 */

// ──────────────────────────────────────────────────────────────────────────
//  Реестр полей ядра v1: профиль + подписки + платежи + теги + активность
// ──────────────────────────────────────────────────────────────────────────

/**
 * Тип значения поля — определяет, какой UI-контрол и какой набор операторов
 * применим, и как резолвер интерпретирует value.
 */
export const segmentFieldKindEnum = z.enum([
  'string', // текстовый ILIKE / точное совпадение (имя, email, username)
  'number', // числовой диапазон/сравнение (totalSpent, purchaseCount)
  'boolean', // флаг (isActive, isEmailVerified)
  'date', // дата/диапазон дат (createdAt, firstPaymentAt)
  'enum', // фиксированный набор значений (статус подписки, провайдер платежа)
  'reference', // ссылка на справочник, грузится с backend (tagIds, planId, courseId)
  // Двухуровневый выбор «продукт + опц. тариф»: курс/книга/канал, и опционально
  // конкретный тариф (PricingTier) этого продукта. Значение —
  // SegmentProductRef (или массив для in/notIn). Используется для доступа
  // и покупок (courseAccess/coursePurchased/bookAccess/...).
  'productRef',
]);
export type SegmentFieldKind = z.infer<typeof segmentFieldKindEnum>;

/**
 * Операторы условия. Набор допустимых операторов зависит от kind поля
 * (см. FIELD_OPERATORS ниже) — Zod проверяет это на уровне superRefine.
 */
export const segmentOperatorEnum = z.enum([
  'eq', // равно
  'neq', // не равно
  'contains', // ILIKE %value% (только string)
  'in', // значение входит в список (enum/reference, multi)
  'notIn', // значение не входит в список
  'gt', // больше (number/date)
  'gte', // больше или равно
  'lt', // меньше (number/date)
  'lte', // меньше или равно
  'between', // в диапазоне [from, to] (number/date)
  'isSet', // поле заполнено (NOT NULL)
  'isNotSet', // поле пустое (NULL)
  'withinLastDays', // активность за последние N дней (date/activity)
  // — Операторы для productRef (доступ/покупки) —
  'hasActive', // есть активный (не истёкший) доступ к продукту
  'expired', // доступ был, но истёк (AccessStatus=EXPIRED или endsAt в прошлом)
  'revoked', // доступ отозван администратором (AccessStatus=REVOKED)
  'purchased', // продукт куплен (AccessGrant.sourceType=ORDER, опц. с тарифом)
  'notPurchased', // продукт НЕ куплен (нет ORDER-грантов на продукт)
  'completed', // курс пройден (CourseProgress.completedAt IS NOT NULL)
  'notCompleted', // курс не пройден (нет записи или completedAt IS NULL)
  'started', // курс начат (есть CourseProgress, прогресс > 0)
]);
export type SegmentOperator = z.infer<typeof segmentOperatorEnum>;

/**
 * Идентификаторы полей ядра. Источник (колонка User или EXISTS-подзапрос
 * по связанной таблице) знает только резолвер — здесь только контракт имён.
 */
export const segmentFieldEnum = z.enum([
  // — Профиль / идентичность —
  'id',
  'firstName',
  'lastName',
  'username',
  'email',
  'phone',
  'telegramId',
  'isEmailVerified',
  'languagePreference',
  'lastCountry',
  'isActive',
  'isPublicProfile',
  'createdAt',
  // — Статус аккаунта —
  'accountStatus', // active | blocked | deleted (виртуальное поле)
  'blockReasonCode',
  // — Атрибуция —
  'acquisitionChannel',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  // — Абонементы (EXISTS по Subscription; subscription-plans = «Абонементы») —
  'subscriptionStatus',
  'subscriptionPlanId',
  'subscriptionPlanTier', // абонемент с уточнением тарифа (productRef) — hasActive/expired/revoked
  'subscriptionEndDate',
  'isFamilyOwner',
  // — Платежи (денормализованные поля User + EXISTS по Payment) —
  'totalSpentBase',
  'purchaseCount',
  'firstPaymentAt',
  'lastPaymentAt',
  'paymentProvider',
  'paymentStatus',
  // — Теги (EXISTS по UserTag) —
  'tagIds',
  // — Активность (EXISTS по UserPresenceLog/UserSession) —
  'onlineWithinDays',
  'lastSessionAt',
  // — Доступ к продуктам (EXISTS по AccessGrant) —
  'courseAccess', // доступ к курсу (любой источник: покупка/подписка/выдача)
  'bookAccess', // доступ к книге
  'channelAccess', // доступ по подписке в канал (channel pass)
  // — Покупки продуктов (AccessGrant.sourceType=ORDER + опц. PricingTier) —
  'coursePurchased', // курс куплен (опционально — конкретным тарифом)
  'bookPurchased', // книга куплена
  'channelPurchased', // подписка в канал куплена
  // — Прогресс по курсам (EXISTS по CourseProgress) —
  'courseCompleted', // курс пройден (completedAt IS NOT NULL)
  'courseStarted', // курс начат (есть прогресс)
]);
export type SegmentField = z.infer<typeof segmentFieldEnum>;

/**
 * Метаданные поля для UI-конструктора и резолвера.
 * `optionsResource` — Refine-ресурс, из которого UI грузит опции для kind=reference.
 */
export interface SegmentFieldMeta {
  field: SegmentField;
  label: string;
  kind: SegmentFieldKind;
  operators: SegmentOperator[];
  /** Для kind=enum — допустимые значения (value+label). */
  enumOptions?: { value: string; label: string }[];
  /**
   * Для kind=reference / productRef — Refine-ресурс справочника продукта
   * (useList): 'courses' | 'books' | 'channel-passes' | 'tags' | ...
   */
  optionsResource?: string;
  /**
   * Для kind=productRef — поддерживает ли поле уточнение тарифом.
   * AccessGrant с sourceType=ORDER → OrderItem.pricingTierId.
   * Тарифы грузятся ресурсом 'pricing-tiers', отфильтрованным по продукту.
   */
  supportsTier?: boolean;
  /** Группа в UI (визуальная категоризация конструктора). */
  group:
    | 'Профиль'
    | 'Статус'
    | 'Атрибуция'
    | 'Абонементы'
    | 'Платежи'
    | 'Теги'
    | 'Активность'
    | 'Доступ'
    | 'Покупки'
    | 'Прогресс';
}

const SET_OPS: SegmentOperator[] = ['isSet', 'isNotSet'];
const STRING_OPS: SegmentOperator[] = ['eq', 'neq', 'contains', ...SET_OPS];
const NUMBER_OPS: SegmentOperator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', ...SET_OPS];
const DATE_OPS: SegmentOperator[] = ['gt', 'gte', 'lt', 'lte', 'between', 'withinLastDays', ...SET_OPS];
const ENUM_OPS: SegmentOperator[] = ['eq', 'neq', 'in', 'notIn', ...SET_OPS];
const BOOL_OPS: SegmentOperator[] = ['eq'];
const REF_OPS: SegmentOperator[] = ['in', 'notIn'];
// Доступ к продукту: активен / истёк / отозван (опц. с уточнением тарифа).
const ACCESS_OPS: SegmentOperator[] = ['hasActive', 'expired', 'revoked'];
// Покупка продукта: куплен / не куплен (опц. конкретным тарифом).
const PURCHASE_OPS: SegmentOperator[] = ['purchased', 'notPurchased'];
// Прогресс по курсу: пройден / не пройден / начат.
const PROGRESS_OPS: SegmentOperator[] = ['completed', 'notCompleted', 'started'];

/**
 * РЕЕСТР ПОЛЕЙ — единственный источник правды.
 * UI рисует конструктор по нему; резолвер маппит field→SQL по нему же.
 */
export const SEGMENT_FIELD_REGISTRY: Record<SegmentField, SegmentFieldMeta> = {
  // Профиль
  id: { field: 'id', label: 'ID пользователя', kind: 'number', operators: NUMBER_OPS, group: 'Профиль' },
  firstName: { field: 'firstName', label: 'Имя', kind: 'string', operators: STRING_OPS, group: 'Профиль' },
  lastName: { field: 'lastName', label: 'Фамилия', kind: 'string', operators: STRING_OPS, group: 'Профиль' },
  username: { field: 'username', label: 'Username', kind: 'string', operators: STRING_OPS, group: 'Профиль' },
  email: { field: 'email', label: 'Email', kind: 'string', operators: STRING_OPS, group: 'Профиль' },
  phone: { field: 'phone', label: 'Телефон', kind: 'string', operators: STRING_OPS, group: 'Профиль' },
  telegramId: { field: 'telegramId', label: 'Telegram ID', kind: 'number', operators: [...NUMBER_OPS], group: 'Профиль' },
  isEmailVerified: { field: 'isEmailVerified', label: 'Email подтверждён', kind: 'boolean', operators: BOOL_OPS, group: 'Профиль' },
  languagePreference: {
    field: 'languagePreference',
    label: 'Язык интерфейса',
    kind: 'reference',
    operators: REF_OPS,
    optionsResource: 'languages',
    group: 'Профиль',
  },
  lastCountry: { field: 'lastCountry', label: 'Страна (последний вход)', kind: 'string', operators: [...STRING_OPS], group: 'Профиль' },
  isActive: { field: 'isActive', label: 'Аккаунт активен', kind: 'boolean', operators: BOOL_OPS, group: 'Профиль' },
  isPublicProfile: { field: 'isPublicProfile', label: 'Публичный профиль', kind: 'boolean', operators: BOOL_OPS, group: 'Профиль' },
  createdAt: { field: 'createdAt', label: 'Дата регистрации', kind: 'date', operators: DATE_OPS, group: 'Профиль' },
  // Статус
  accountStatus: {
    field: 'accountStatus',
    label: 'Статус аккаунта',
    kind: 'enum',
    operators: ['eq', 'neq', 'in'],
    enumOptions: [
      { value: 'active', label: 'Активен' },
      { value: 'blocked', label: 'Заблокирован' },
      { value: 'deleted', label: 'Удалён' },
    ],
    group: 'Статус',
  },
  blockReasonCode: {
    field: 'blockReasonCode',
    label: 'Причина блокировки',
    kind: 'enum',
    operators: ENUM_OPS,
    enumOptions: [
      { value: 'SPAM', label: 'Спам' },
      { value: 'ABUSE', label: 'Злоупотребление' },
      { value: 'FRAUD', label: 'Мошенничество' },
      { value: 'PAYMENT_CHARGEBACK', label: 'Чарджбэк' },
      { value: 'TOS_VIOLATION', label: 'Нарушение правил' },
      { value: 'OTHER', label: 'Другое' },
    ],
    group: 'Статус',
  },
  // Атрибуция
  acquisitionChannel: { field: 'acquisitionChannel', label: 'Канал привлечения', kind: 'string', operators: STRING_OPS, group: 'Атрибуция' },
  utmSource: { field: 'utmSource', label: 'UTM Source', kind: 'string', operators: STRING_OPS, group: 'Атрибуция' },
  utmMedium: { field: 'utmMedium', label: 'UTM Medium', kind: 'string', operators: STRING_OPS, group: 'Атрибуция' },
  utmCampaign: { field: 'utmCampaign', label: 'UTM Campaign', kind: 'string', operators: STRING_OPS, group: 'Атрибуция' },
  // Абонементы (subscription-plans = «Абонементы» в терминологии проекта)
  subscriptionStatus: {
    field: 'subscriptionStatus',
    label: 'Статус абонемента',
    kind: 'enum',
    operators: ENUM_OPS,
    enumOptions: [
      { value: 'PENDING', label: 'Ожидает' },
      { value: 'ACTIVE', label: 'Активен' },
      { value: 'EXPIRED', label: 'Истёк' },
      { value: 'CANCELLED', label: 'Отменён' },
    ],
    group: 'Абонементы',
  },
  subscriptionPlanId: {
    field: 'subscriptionPlanId',
    label: 'Абонемент',
    kind: 'reference',
    operators: REF_OPS,
    optionsResource: 'subscription-plans',
    group: 'Абонементы',
  },
  // Абонемент с уточнением тарифа: EXISTS по Subscription c матчингом
  // subscriptionPlanId (+ опц. pricingTierId). Операторы по статусу:
  // hasActive — есть подписка status=ACTIVE и (endDate IS NULL OR endDate > NOW())
  // expired   — есть подписка status=EXPIRED ИЛИ endDate в прошлом
  // revoked   — есть подписка status=CANCELLED
  subscriptionPlanTier: {
    field: 'subscriptionPlanTier',
    label: 'Абонемент (с тарифом)',
    kind: 'productRef',
    operators: ACCESS_OPS,
    optionsResource: 'subscription-plans',
    supportsTier: true,
    group: 'Абонементы',
  },
  subscriptionEndDate: { field: 'subscriptionEndDate', label: 'Абонемент действует до', kind: 'date', operators: DATE_OPS, group: 'Абонементы' },
  isFamilyOwner: { field: 'isFamilyOwner', label: 'Владелец семейного абонемента', kind: 'boolean', operators: BOOL_OPS, group: 'Абонементы' },
  // Платежи
  totalSpentBase: { field: 'totalSpentBase', label: 'Всего потрачено', kind: 'number', operators: NUMBER_OPS, group: 'Платежи' },
  purchaseCount: { field: 'purchaseCount', label: 'Количество покупок', kind: 'number', operators: NUMBER_OPS, group: 'Платежи' },
  firstPaymentAt: { field: 'firstPaymentAt', label: 'Дата первого платежа', kind: 'date', operators: DATE_OPS, group: 'Платежи' },
  lastPaymentAt: { field: 'lastPaymentAt', label: 'Дата последнего платежа', kind: 'date', operators: DATE_OPS, group: 'Платежи' },
  paymentProvider: {
    field: 'paymentProvider',
    label: 'Провайдер платежа',
    kind: 'enum',
    operators: ENUM_OPS,
    enumOptions: [
      { value: 'MANUAL', label: 'Вручную' },
      { value: 'CLICK', label: 'Click' },
      { value: 'PAYME', label: 'Payme' },
      { value: 'STRIPE', label: 'Stripe' },
      { value: 'MULTICARD', label: 'Multicard' },
      { value: 'CLOUDPAYMENTS', label: 'CloudPayments' },
    ],
    group: 'Платежи',
  },
  paymentStatus: {
    field: 'paymentStatus',
    label: 'Статус платежа',
    kind: 'enum',
    operators: ENUM_OPS,
    enumOptions: [
      { value: 'PENDING', label: 'Ожидает' },
      { value: 'CONFIRMED', label: 'Подтверждён' },
      { value: 'REJECTED', label: 'Отклонён' },
      { value: 'REFUNDED', label: 'Возвращён' },
      { value: 'PARTIALLY_REFUNDED', label: 'Частично возвращён' },
    ],
    group: 'Платежи',
  },
  // Теги
  tagIds: {
    field: 'tagIds',
    label: 'Теги',
    kind: 'reference',
    operators: REF_OPS,
    optionsResource: 'tags',
    group: 'Теги',
  },
  // Активность
  onlineWithinDays: { field: 'onlineWithinDays', label: 'Был онлайн за последние N дней', kind: 'number', operators: ['lte', 'withinLastDays'], group: 'Активность' },
  lastSessionAt: { field: 'lastSessionAt', label: 'Последняя сессия', kind: 'date', operators: DATE_OPS, group: 'Активность' },
  // Доступ — AccessGrant (любой источник: покупка/подписка/выдача)
  courseAccess: {
    field: 'courseAccess',
    label: 'Доступ к курсу',
    kind: 'productRef',
    operators: ACCESS_OPS,
    optionsResource: 'courses',
    supportsTier: true,
    group: 'Доступ',
  },
  bookAccess: {
    field: 'bookAccess',
    label: 'Доступ к книге',
    kind: 'productRef',
    operators: ACCESS_OPS,
    optionsResource: 'books',
    supportsTier: true,
    group: 'Доступ',
  },
  channelAccess: {
    field: 'channelAccess',
    label: 'Доступ по подписке в канал',
    kind: 'productRef',
    operators: ACCESS_OPS,
    optionsResource: 'channel-passes',
    supportsTier: true,
    group: 'Доступ',
  },
  // Покупки — AccessGrant.sourceType=ORDER (+ опц. тариф через OrderItem)
  coursePurchased: {
    field: 'coursePurchased',
    label: 'Куплен курс',
    kind: 'productRef',
    operators: PURCHASE_OPS,
    optionsResource: 'courses',
    supportsTier: true,
    group: 'Покупки',
  },
  bookPurchased: {
    field: 'bookPurchased',
    label: 'Куплена книга',
    kind: 'productRef',
    operators: PURCHASE_OPS,
    optionsResource: 'books',
    supportsTier: true,
    group: 'Покупки',
  },
  channelPurchased: {
    field: 'channelPurchased',
    label: 'Куплена подписка в канал',
    kind: 'productRef',
    operators: PURCHASE_OPS,
    optionsResource: 'channel-passes',
    supportsTier: true,
    group: 'Покупки',
  },
  // Прогресс — CourseProgress
  courseCompleted: {
    field: 'courseCompleted',
    label: 'Курс пройден',
    kind: 'productRef',
    operators: PROGRESS_OPS,
    optionsResource: 'courses',
    supportsTier: false,
    group: 'Прогресс',
  },
  courseStarted: {
    field: 'courseStarted',
    label: 'Курс начат',
    kind: 'productRef',
    operators: PROGRESS_OPS,
    optionsResource: 'courses',
    supportsTier: false,
    group: 'Прогресс',
  },
};

// ──────────────────────────────────────────────────────────────────────────
//  Схема дерева сегмента
// ──────────────────────────────────────────────────────────────────────────

/**
 * Значение условия. Дискриминируется на уровне резолвера по
 * SEGMENT_FIELD_REGISTRY[field].kind. Здесь — широкий union,
 * superRefine ниже проверяет согласованность field/operator/value.
 */
/**
 * Ссылка на продукт для kind=productRef. targetId — id курса/книги/канала
 * (из optionsResource поля). tierId — опциональное уточнение тарифом
 * (PricingTier, через AccessGrant.orderId → OrderItem.pricingTierId);
 * отсутствие tierId означает «любой тариф / неважно».
 */
export const segmentProductRefSchema = z.object({
  targetId: z.number().int().positive(),
  tierId: z.number().int().positive().optional(),
});
export type SegmentProductRef = z.infer<typeof segmentProductRefSchema>;

export const segmentConditionValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
  // productRef: одиночный продукт или список (для будущего multi)
  segmentProductRefSchema,
  z.array(segmentProductRefSchema),
  // Диапазон для between: [from, to]
  z.tuple([z.union([z.string(), z.number()]), z.union([z.string(), z.number()])]),
  z.null(),
]);
export type SegmentConditionValue = z.infer<typeof segmentConditionValueSchema>;

export const segmentConditionSchema = z
  .object({
    field: segmentFieldEnum,
    operator: segmentOperatorEnum,
    value: segmentConditionValueSchema.optional(),
  })
  .superRefine((cond, ctx) => {
    const meta = SEGMENT_FIELD_REGISTRY[cond.field];
    if (!meta.operators.includes(cond.operator)) {
      ctx.addIssue({
        code: 'custom',
        message: `Оператор «${cond.operator}» недопустим для поля «${meta.label}»`,
        path: ['operator'],
      });
    }
    const valuelessOps: SegmentOperator[] = ['isSet', 'isNotSet'];
    if (!valuelessOps.includes(cond.operator) && cond.value === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: `Для оператора «${cond.operator}» требуется значение`,
        path: ['value'],
      });
    }
    if (meta.kind === 'productRef' && cond.value !== undefined) {
      const single = segmentProductRefSchema.safeParse(cond.value);
      const list = z.array(segmentProductRefSchema).min(1).safeParse(cond.value);
      if (!single.success && !list.success) {
        ctx.addIssue({
          code: 'custom',
          message: 'Выберите продукт (и при необходимости тариф)',
          path: ['value'],
        });
      }
    }
    if (cond.operator === 'between') {
      const isTuple = Array.isArray(cond.value) && cond.value.length === 2;
      if (!isTuple) {
        ctx.addIssue({
          code: 'custom',
          message: 'Для диапазона требуется пара значений [от, до]',
          path: ['value'],
        });
      }
    }
  });
export type SegmentCondition = z.infer<typeof segmentConditionSchema>;

export const segmentLogicalOpEnum = z.enum(['AND', 'OR']);
export type SegmentLogicalOp = z.infer<typeof segmentLogicalOpEnum>;

export const segmentGroupSchema = z.object({
  conditionsOp: segmentLogicalOpEnum.default('AND'),
  conditions: z
    .array(segmentConditionSchema)
    .min(1, 'Группа должна содержать хотя бы одно условие')
    .max(50, 'Слишком много условий в группе (максимум 50)'),
});
export type SegmentGroup = z.infer<typeof segmentGroupSchema>;

export const segmentDefinitionSchema = z.object({
  groupsOp: segmentLogicalOpEnum.default('AND'),
  groups: z
    .array(segmentGroupSchema)
    .max(20, 'Слишком много групп (максимум 20)')
    // Пустой массив групп = «вся аудитория» (без ограничений) — это валидно.
    .default([]),
});
export type SegmentDefinition = z.infer<typeof segmentDefinitionSchema>;

// ──────────────────────────────────────────────────────────────────────────
//  Сохраняемые сегменты (пресеты) — таблица UserSegment
// ──────────────────────────────────────────────────────────────────────────

/**
 * Режим сегмента:
 *  - DYNAMIC: хранится дерево правил, аудитория резолвится каждый раз заново
 *    (актуальная — «все с активной подпиской»).
 *  - STATIC: дерево резолвится один раз в момент сохранения, фиксируется
 *    снимок userId (предсказуемая аудитория, не меняется).
 */
export const segmentModeEnum = z.enum(['DYNAMIC', 'STATIC']);
export type SegmentMode = z.infer<typeof segmentModeEnum>;

export const createUserSegmentSchema = z.object({
  name: z
    .string({ message: 'Название обязательно' })
    .min(1, 'Название обязательно')
    .max(200, 'Название слишком длинное (максимум 200 символов)'),
  description: z.string().max(1000).optional(),
  mode: segmentModeEnum.default('DYNAMIC'),
  definition: segmentDefinitionSchema,
});
export type CreateUserSegmentInput = z.infer<typeof createUserSegmentSchema>;

export const updateUserSegmentSchema = createUserSegmentSchema.partial();
export type UpdateUserSegmentInput = z.infer<typeof updateUserSegmentSchema>;

/**
 * Контракт запроса резолва аудитории (используется users/broadcasts/promo/
 * livestream). Либо ссылка на сохранённый сегмент (segmentId), либо инлайн
 * definition. snapshotUserIds — явно добавленные/исключённые пользователи
 * поверх правил (как `userIds` в старых LivestreamAudienceFilters — логика OR).
 */
export const audienceQuerySchema = z
  .object({
    segmentId: z.number().int().positive().optional(),
    definition: segmentDefinitionSchema.optional(),
    /** Явно включённые пользователи (OR поверх правил). */
    includeUserIds: z.array(z.number().int().positive()).max(100_000).optional(),
    /** Явно исключённые пользователи (минус из результата). */
    excludeUserIds: z.array(z.number().int().positive()).max(100_000).optional(),
  })
  .refine((q) => q.segmentId !== undefined || q.definition !== undefined || (q.includeUserIds?.length ?? 0) > 0, {
    message: 'Требуется segmentId, definition или includeUserIds',
  });
export type AudienceQuery = z.infer<typeof audienceQuerySchema>;
